/* ──────────────────────────────────────────────────────────────
   src/hooks/useAvailability.ts
   Hooks React‑Query para consultar disponibilidad de profesores
   · useAvailability           → profesores que YA imparten (STRICT | FLEXIBLE)
   · useAvailabilityFormables  → candidatos a formarse (siempre FLEXIBLE)
   ────────────────────────────────────────────────────────────── */

import { useQuery } from '@tanstack/react-query';
import api from '../api/axios';

/* ═════════════ tipos que ENVÍA el front ═════════════ */

export type SearchMode = 'STRICT' | 'FLEXIBLE';

export interface AvailabilityDto {
  /* rango + filtros obligatorios */
  startDate: string; // YYYY‑MM‑DD
  endDate: string; // YYYY‑MM‑DD
  asignaturaId: number;
  diasSemana: number[]; // 0‑6  (0 = lunes)
  horaDesde: string; // HH:mm
  horaHasta: string; // HH:mm
  duracionMin: number; // minutos

  /* filtros opcionales */
  centroId?: number;
  formatoId?: number;
  profesorId?: number;
  idiomaId?: number;

  /* parámetros de ajuste */
  clasesSemana?: number; // default = 1 (backend)
  threshold?: number; // default = 85
  searchMode?: SearchMode; // default = FLEXIBLE
}

/* ═════════════ tipos que RECIBE el front ═════════════ */

export type WeekIssueReason = 'NO_SLOTS' | 'SLOTS_INSUF' | 'DAY_MISSING' | 'TOPE_HORAS';

export interface WeekIssue {
  week: string; // YYYY‑WW (ISO‑8601)
  reason: WeekIssueReason;
}

export interface Intervalo {
  ini: string; // ISO
  fin: string;
}

export interface ResultadoOk {
  profesorId: number;
  intervalos: Intervalo[];

  /* patrón óptimo (modo FLEXIBLE) o fijo (modo STRICT) */
  pattern: { day: number; time: string }[];
  patternRatio: number; // % semanas cubiertas por pattern

  /* semáforo L‑D */
  dayStatus: ('green' | 'amber' | 'red')[]; // longitud 7

  /* NUEVO: hasta 4 propuestas de patrón (solo FLEXIBLE) */
  suggestions?: { day: number; time: string }[][];

  unavailableWeeks: WeekIssue[];
  ratio: number; // % semanas OK
}

export interface ResultadoKo {
  profesorId: number;
  motivo: 'PORCENTAJE' | 'SIN_CANDIDATURA';
  unavailableWeeks: WeekIssue[];
  ratio: number;
}

export interface AvailabilityResponse {
  ok: ResultadoOk[];
  descartados: ResultadoKo[];
}

/* ═════════════ helper genérico ═════════════ */

function useAvailabilityGeneric(endpoint: string, params?: AvailabilityDto) {
  /* react‑query necesita una clave determinística */
  const key: [string, string | null] = [endpoint, params ? JSON.stringify(params) : null];

  const {
    data = { ok: [], descartados: [] },
    isFetching: loading,
    error,
    ...rest
  } = useQuery<AvailabilityResponse, Error, AvailabilityResponse, typeof key>({
    queryKey: key,
    enabled: Boolean(params), // solo si form completo
    placeholderData: (old) => old, // no parpadeo de datos
    queryFn: async () => {
      const { data } = await api.post<AvailabilityResponse>(endpoint, params);
      return data;
    },
    staleTime: 60_000, // 1 min sin refetch
  });

  return { ...data, loading, error, ...rest };
}

/* ═════════════ hooks públicos ═════════════ */

/** Profesores que YA imparten la asignatura.  */
export function useAvailability(params?: AvailabilityDto) {
  return useAvailabilityGeneric('/disponibilidad-consulta', params);
}

/** Profesores que PUEDEN formarse para la asignatura. */
export function useAvailabilityFormables(params?: AvailabilityDto) {
  return useAvailabilityGeneric('/disponibilidad-consulta/formables', params);
}

/* garantiza que el archivo se trate como módulo */
export {};
